import { NavLink } from "react-router-dom";
import { clsx } from "clsx";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Wallet,
  LayoutDashboard,
  Search,
  Image,
  ImagePlus,
  Palette,
  Store,
  Sparkles,
  Settings,
  LogOut,
  ShoppingBag,
  Package,
  ShoppingCart,
  MessageSquare,
  TrendingUp,
  BarChart3,
  Layers,
  FileStack,
  Calculator,
  Clock,
  Boxes,
  Warehouse,
  Images,
  ClipboardList,
  Pencil,
  Check,
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  X,
  FolderPlus,
  Star,
  Video,
  Bot,
  Pin,
  Radar,
  ListChecks,
  Shirt,
} from "lucide-react";
import { useAuthStore } from "../../store/auth.store";
import { useShopVertical } from "../../lib/useShopVertical";

const ICON_MAP: Record<string, React.ElementType> = {
  LayoutDashboard, Search, Image, ImagePlus, Palette, Store, Settings,
  ShoppingBag, Package, ShoppingCart, MessageSquare, TrendingUp, BarChart3,
  Layers, FileStack, Calculator, Clock, Boxes, Warehouse, Images, ClipboardList,
  Sparkles, LogOut, Pencil, Check, Plus, Trash2, GripVertical, Star, Video, Wallet, Bot, Pin, Radar, ListChecks, Shirt,
};

interface NavItem {
  label: string;
  href: string;
  icon: string; // icon name as string for serialization
}

interface NavSection {
  id: string;
  title?: string;
  items: NavItem[];
}

const DEFAULT_SECTIONS: NavSection[] = [
  {
    id: "main",
    items: [
      { label: "Dashboard", href: "/dashboard", icon: "LayoutDashboard" },
      { label: "Planlarım", href: "/plans", icon: "ClipboardList" },
    ],
  },
  {
    id: "olustur",
    title: "Oluştur",
    items: [
      { label: "Listing Builder", href: "/listing-builder", icon: "ShoppingBag" },
      { label: "Custom Listeler", href: "/custom", icon: "Shirt" },
      { label: "Design Builder", href: "/design-builder", icon: "Palette" },
      { label: "Tasarım Motoru", href: "/design-engine", icon: "Sparkles" },
      { label: "Mockup Studio", href: "/mockup-studio", icon: "Image" },
      { label: "Video Studio", href: "/video-studio", icon: "Video" },
      { label: "Tasarımlar", href: "/designs", icon: "Images" },
      { label: "Şablonlar", href: "/templates", icon: "FileStack" },
      { label: "Tedarikçi Katalog", href: "/supplier-catalogs", icon: "Warehouse" },
    ],
  },
  {
    id: "magaza",
    title: "Mağaza",
    items: [
      { label: "Mağazalarım", href: "/my-shops", icon: "Store" },
      { label: "Satış Raporu", href: "/sales-dashboard", icon: "BarChart3" },
      { label: "İstatistikler", href: "/stats", icon: "BarChart3" },
      { label: "Etsy Ürünlerim", href: "/etsy/products", icon: "Package" },
      { label: "Envanter", href: "/inventory", icon: "Boxes" },
      { label: "Siparişler", href: "/etsy/orders", icon: "ShoppingCart" },
      { label: "Yorumlar", href: "/etsy/reviews", icon: "MessageSquare" },
      { label: "Alıcı Mesajları", href: "/etsy/messages", icon: "MessageSquare" },
      { label: "Mağaza Vitrini", href: "/storefront", icon: "Store" },
      { label: "Pinterest", href: "/pinterest", icon: "Pin" },
      { label: "Toplu İşlem", href: "/bulk-operations", icon: "Layers" },
      { label: "Zamanlayıcı", href: "/scheduler", icon: "Clock" },
    ],
  },
  {
    id: "arastirma",
    title: "Araştırma",
    items: [
      { label: "Trend Intelligence", href: "/trend-intelligence", icon: "TrendingUp" },
      { label: "Konseptler", href: "/concepts", icon: "Layers" },
      { label: "Ürün Analizleri", href: "/analyses", icon: "ClipboardList" },
      { label: "eRank Analiz", href: "/erank", icon: "BarChart3" },
      { label: "Ürün Keşfi", href: "/discovery", icon: "Radar" },
      { label: "Eklenti Görevleri", href: "/tasks", icon: "ListChecks" },
      { label: "Yeni Analiz", href: "/analyses/new", icon: "Search" },
      { label: "Fiyat Asistanı", href: "/pricing", icon: "Calculator" },
      { label: "Mağaza Takip", href: "/shops", icon: "Store" },
    ],
  },
  {
    id: "diger",
    title: "Diğer",
    items: [
      { label: "Mockup Yönetimi", href: "/mockup-manager", icon: "ImagePlus" },
      { label: "AI Kullanımı", href: "/ai-usage", icon: "BarChart3" },
      { label: "ChatGPT / MCP", href: "/chatgpt", icon: "Bot" },
      { label: "Ayarlar", href: "/settings", icon: "Settings" },
    ],
  },
];

// TAKI MENUSU — Trendyol magazalari ve Etsy taki magazalari icin ayni mantik.
//
// Taki hattinda tasarim havuzu, bos mockup ve tasarim uretimi kavramlari yok:
// urun tedarikciden hazir gelir, gorsel konsept fotografiyla uretilir. Bu yuzden
// tekstil menusunu suzmek yerine KENDI sabit menusu var. Kullanicinin tekstil
// menusunde yaptigi duzenlemeler bu setten etkilenmez.
//
// Etsy taki setinde Trendyol'da karsiligi olmayan Etsy ekranlari da var
// (siparisler, satis raporu) — akis ayni, magaza ekranlari platforma gore.
function jewelrySections(platform: string): NavSection[] {
  const isTrendyol = platform === "trendyol";
  const shopItems: NavItem[] = isTrendyol
    ? [
        { label: "Mağazalarım", href: "/my-shops", icon: "Store" },
        { label: "AI Kullanımı", href: "/ai-usage", icon: "BarChart3" },
        { label: "Ayarlar", href: "/settings", icon: "Settings" },
      ]
    : [
        { label: "Mağazalarım", href: "/my-shops", icon: "Store" },
        { label: "Siparişler", href: "/etsy/orders", icon: "ShoppingCart" },
        { label: "Satış Raporu", href: "/sales-dashboard", icon: "BarChart3" },
        { label: "İstatistikler", href: "/stats", icon: "BarChart3" },
        // Etsy'de yayina cikan listelerin pin sirasi (magaza dikeyine gore suzulur)
        { label: "Pinterest", href: "/pinterest", icon: "Pin" },
        // Konsept fotografi uretimi de AI harcamasi — takida da gorulsun
        { label: "AI Kullanımı", href: "/ai-usage", icon: "BarChart3" },
        { label: "Ayarlar", href: "/settings", icon: "Settings" },
      ];
  return [
    {
      id: "jw-main",
      items: [{ label: "Dashboard", href: "/jewelry-dashboard", icon: "LayoutDashboard" }],
    },
    {
      id: "jw-uret",
      title: "Üret",
      // Akis sirasiyla numarali: urun → sahne → fotograf → urunler → denetim → taslak.
      // Listing Builder: taslaklarin planlanip Etsy'de yayinlandigi ekran.
      items: [
        ...([
          { label: "Katalog", href: "/supplier-catalogs", icon: "Warehouse" },
          { label: "Konsept Fotoğrafları", href: "/mockup-manager", icon: "ImagePlus" },
          { label: "Studio", href: "/jewelry-studio", icon: "Sparkles" },
          { label: "Toplu Studio", href: "/jewelry-studio/bulk", icon: "Layers" },
          // Fotografi uretilmis urunler (listesi olmayanlar one cikar)
          { label: "Ürünler", href: "/jewelry-products", icon: "Package" },
          // Etsy taslagina toplu cevirme — Trendyol'da karsiligi yok
          ...(isTrendyol ? [] : [
            { label: "Liste Denetimi", href: "/listing-review/jewelry", icon: "ClipboardCheck" },
            { label: "Listing Builder", href: "/listing-builder", icon: "ShoppingBag" },
          ]),
          { label: "Taslaklar", href: "/listing-drafts", icon: "ClipboardList" },
        ] as NavItem[]).map((it, i) => ({ ...it, label: `${i + 1} · ${it.label}` })),
        // Urun fotograflarindan video (ComfyUI LTX) — Etsy listesine video
        { label: "Video Studio", href: "/video-studio", icon: "Video" },
        { label: "Şablonlar", href: "/templates", icon: "FileStack" },
      ],
    },
    { id: "jw-magaza", title: "Mağaza", items: shopItems },
  ];
}

const SHOW_ALL_KEY = "erc-sidebar-show-all";

const STORAGE_KEY = "erc-sidebar-config";
// Kayitli config'i olan mevcut kullanicilara yeni default menu ogeleri icin tek seferlik enjeksiyon.
// Yeni bir default item eklendiginde bu anahtari artir ve MIGRATIONS listesine ekle.
const MIGRATION_KEY = "erc-sidebar-migration";
const MIGRATION_VERSION = 17;
const MIGRATIONS: Array<{ sectionId: string; item: NavItem; afterHref?: string }> = [
  { sectionId: "arastirma", item: { label: "Konseptler", href: "/concepts", icon: "Layers" }, afterHref: "/trend-intelligence" },
  { sectionId: "arastirma", item: { label: "Ürün Analizleri", href: "/analyses", icon: "ClipboardList" }, afterHref: "/concepts" },
  { sectionId: "arastirma", item: { label: "eRank Analiz", href: "/erank", icon: "BarChart3" }, afterHref: "/analyses" },
  // Coklu magaza: kendi satis magazalarinin yonetimi (rakip takibi /shops ayri)
  { sectionId: "magaza", item: { label: "Mağazalarım", href: "/my-shops", icon: "Store" } },
  // Yerel LTX-2.3 ile gorselden video uretimi
  { sectionId: "olustur", item: { label: "Video Studio", href: "/video-studio", icon: "Video" }, afterHref: "/mockup-studio" },
  // AI token/maliyet olcumu
  { sectionId: "diger", item: { label: "AI Kullanımı", href: "/ai-usage", icon: "BarChart3" }, afterHref: "/mockup-manager" },
  // Tasarimlari yayina hazirlama denetimi
  { sectionId: "olustur", item: { label: "Liste Denetimi", href: "/listing-review", icon: "ClipboardCheck" }, afterHref: "/listing-builder" },
  // Siparis/kargo/teslim mesajlari ve yorum yanitlari
  { sectionId: "magaza", item: { label: "Alıcı Mesajları", href: "/etsy/messages", icon: "MessageSquare" }, afterHref: "/etsy/reviews" },
  // Kar-zarar, reklam verimliligi, ilan karliligi ve sagligi
  { sectionId: "magaza", item: { label: "İstatistikler", href: "/stats", icon: "BarChart3" }, afterHref: "/sales-dashboard" },
  // ChatGPT / Claude MCP baglantisi
  { sectionId: "diger", item: { label: "ChatGPT / MCP", href: "/chatgpt", icon: "Bot" }, afterHref: "/ai-usage" },
  // Etsy'de yayina cikan listelerin Pinterest pin sirasi
  { sectionId: "magaza", item: { label: "Pinterest", href: "/pinterest", icon: "Pin" }, afterHref: "/etsy/messages" },
  // Urun baski sablonu uzerinde katmanli tasarim
  { sectionId: "olustur", item: { label: "Tasarım Motoru", href: "/design-engine", icon: "Sparkles" }, afterHref: "/design-builder" },
  // Shop title, duyuru, hikaye, SSS (Noel serisi 4. bolum)
  { sectionId: "magaza", item: { label: "Mağaza Vitrini", href: "/storefront", icon: "Store" }, afterHref: "/etsy/messages" },
  // Eklentinin anahtar kelime taramasi sonuclari
  { sectionId: "arastirma", item: { label: "Ürün Keşfi", href: "/discovery", icon: "Radar" }, afterHref: "/erank" },
  // Panelden eklentiye (VDS) gorev verme
  { sectionId: "arastirma", item: { label: "Eklenti Görevleri", href: "/tasks", icon: "ListChecks" }, afterHref: "/discovery" },
  // Custom Liste Studyosu panosu
  { sectionId: "olustur", item: { label: "Custom Listeler", href: "/custom", icon: "Shirt" }, afterHref: "/listing-builder" },
];

function applyMigrations(sections: NavSection[]): NavSection[] {
  const done = Number(localStorage.getItem(MIGRATION_KEY) || "0");
  if (done >= MIGRATION_VERSION) return sections;

  const next = sections.map((s) => ({ ...s, items: [...s.items] }));
  for (const mig of MIGRATIONS) {
    const exists = next.some((s) => s.items.some((i) => i.href === mig.item.href));
    if (exists) continue;
    let target = next.find((s) => s.id === mig.sectionId);
    if (!target) {
      // Grup silinmisse ilk gruba ekle
      target = next[0];
      if (!target) continue;
    }
    const afterIdx = mig.afterHref ? target.items.findIndex((i) => i.href === mig.afterHref) : -1;
    if (afterIdx >= 0) target.items.splice(afterIdx + 1, 0, mig.item);
    else target.items.push(mig.item);
  }
  // v13: kayitli menudeki ASCII etiketleri ("Siparisler") Turkce karakterli default'a cevir.
  // Yalniz harf farki varsa degisir; kullanicinin kendi verdigi adlar korunur.
  const fold = (t: string) => t.replace(/[çğıöşüÇĞİÖŞÜ]/g, (c) => "cgiosuCGIOSU"["çğıöşüÇĞİÖŞÜ".indexOf(c)]);
  const defaultItems = new Map(DEFAULT_SECTIONS.flatMap((s) => s.items).map((i) => [i.href, i.label]));
  const defaultTitles = new Map(DEFAULT_SECTIONS.map((s) => [s.id, s.title]));
  for (const s of next) {
    const title = defaultTitles.get(s.id);
    if (title && s.title && s.title !== title && fold(s.title) === fold(title)) s.title = title;
    s.items = s.items.map((i) => {
      const label = defaultItems.get(i.href);
      return label && i.label !== label && fold(i.label) === fold(label) ? { ...i, label } : i;
    });
  }
  localStorage.setItem(MIGRATION_KEY, String(MIGRATION_VERSION));
  saveSections(next);
  return next;
}

function loadSections(): NavSection[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as NavSection[];
      // Validate structure - each section must have id and items array
      if (Array.isArray(parsed) && parsed.every(s => s.id && Array.isArray(s.items) && s.items.every(i => i.label && i.href && i.icon))) {
        return applyMigrations(parsed);
      }
      // Invalid data, clear it
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch { localStorage.removeItem(STORAGE_KEY); }
  // Yeni kullanici: default zaten guncel, migration'i uygulanmis say
  localStorage.setItem(MIGRATION_KEY, String(MIGRATION_VERSION));
  return DEFAULT_SECTIONS;
}

function saveSections(sections: NavSection[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
}

const FAV_KEY = "erc-sidebar-favorites";
function loadFavorites(): string[] {
  try {
    const s = localStorage.getItem(FAV_KEY);
    if (s) { const p = JSON.parse(s); if (Array.isArray(p)) return p.filter((x) => typeof x === "string"); }
  } catch { /* ignore */ }
  return [];
}

function getIcon(name: string): React.ElementType {
  return ICON_MAP[name] || LayoutDashboard;
}

export function Sidebar() {
  const { user, logout } = useAuthStore();
  const { platform, shop, isConceptVertical } = useShopVertical();
  const isTrendyol = platform === "trendyol";
  const [showAll, setShowAll] = useState<boolean>(() => localStorage.getItem(SHOW_ALL_KEY) === "1");
  const [sections, setSections] = useState<NavSection[]>(loadSections);
  const [favorites, setFavorites] = useState<string[]>(loadFavorites);
  const [editMode, setEditMode] = useState(false);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [dragItem, setDragItem] = useState<{ sectionId: string; itemIdx: number } | null>(null);
  const [dragOverItem, setDragOverItem] = useState<{ sectionId: string; itemIdx: number } | null>(null);
  const [dragSection, setDragSection] = useState<number | null>(null);
  const [dragOverSection, setDragOverSection] = useState<number | null>(null);

  useEffect(() => {
    saveSections(sections);
  }, [sections]);

  useEffect(() => {
    localStorage.setItem(FAV_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(SHOW_ALL_KEY, showAll ? "1" : "0");
  }, [showAll]);

  // Duzenleme modunda tam menu geri gelir; kullanici gormedigi bir ogeyi
  // tasiyamaz ya da silemez, gizli oge kaybolmus gibi gorunurdu.
  // Taki menusu: Trendyol magazalari + Etsy TAKI magazalari (ayni akis).
  const useJewelryMenu = (isTrendyol || isConceptVertical) && !showAll && !editMode;
  const jewelryMenu = jewelrySections(platform);
  const shownSections = useJewelryMenu ? jewelryMenu : sections;

  const toggleFavorite = (href: string) =>
    setFavorites((prev) => (prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href]));

  // Favori href'leri gercek item'lara cozumle (silinmis olanlari at)
  const allItems = sections.flatMap((s) => s.items);
  const favItems = favorites
    .map((h) => allItems.find((i) => i.href === h))
    .filter((i): i is NavItem => !!i)
    // Taki menusundeyken tekstil favorileri gosterilmez
    .filter((i) => !useJewelryMenu || jewelryMenu.some((sec) => sec.items.some((x) => x.href === i.href)));

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  const updateSections = useCallback((fn: (prev: NavSection[]) => NavSection[]) => {
    setSections(prev => {
      const next = fn(prev);
      return next;
    });
  }, []);

  // Section operations
  const renameSection = (id: string, title: string) => {
    updateSections(prev => prev.map(s => s.id === id ? { ...s, title } : s));
  };

  const addSection = () => {
    const id = `group-${Date.now()}`;
    updateSections(prev => [...prev, { id, title: "Yeni Grup", items: [] }]);
    setEditingTitle(id);
  };

  const removeSection = (id: string) => {
    updateSections(prev => prev.filter(s => s.id !== id));
  };

  const moveSectionUp = (idx: number) => {
    if (idx <= 0) return;
    updateSections(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveSectionDown = (idx: number) => {
    updateSections(prev => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  // Item operations
  const moveItemUp = (sectionId: string, itemIdx: number) => {
    if (itemIdx <= 0) return;
    updateSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      const items = [...s.items];
      [items[itemIdx - 1], items[itemIdx]] = [items[itemIdx], items[itemIdx - 1]];
      return { ...s, items };
    }));
  };

  const moveItemDown = (sectionId: string, itemIdx: number) => {
    updateSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      if (itemIdx >= s.items.length - 1) return s;
      const items = [...s.items];
      [items[itemIdx], items[itemIdx + 1]] = [items[itemIdx + 1], items[itemIdx]];
      return { ...s, items };
    }));
  };

  const removeItem = (sectionId: string, itemIdx: number) => {
    updateSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      return { ...s, items: s.items.filter((_, i) => i !== itemIdx) };
    }));
  };

  // Drag & drop for items between sections
  const handleItemDragStart = (sectionId: string, itemIdx: number) => {
    setDragItem({ sectionId, itemIdx });
  };

  const handleItemDragOver = (e: React.DragEvent, sectionId: string, itemIdx: number) => {
    e.preventDefault();
    setDragOverItem({ sectionId, itemIdx });
  };

  const handleItemDragOverSection = (e: React.DragEvent, sectionId: string) => {
    e.preventDefault();
    if (dragItem) {
      setDragOverItem({ sectionId, itemIdx: -1 });
    }
  };

  const handleItemDrop = () => {
    if (!dragItem || !dragOverItem) { setDragItem(null); setDragOverItem(null); return; }
    const { sectionId: fromSec, itemIdx: fromIdx } = dragItem;
    const { sectionId: toSec, itemIdx: toIdx } = dragOverItem;

    updateSections(prev => {
      const next = prev.map(s => ({ ...s, items: [...s.items] }));
      const fromSection = next.find(s => s.id === fromSec);
      const toSection = next.find(s => s.id === toSec);
      if (!fromSection || !toSection) return prev;
      if (fromIdx < 0 || fromIdx >= fromSection.items.length) return prev;

      // Save the item before removing
      const item = { ...fromSection.items[fromIdx] };
      fromSection.items.splice(fromIdx, 1);

      // Adjust target index if same section and target is after source
      let insertIdx = toIdx === -1 ? toSection.items.length : toIdx;
      if (fromSec === toSec && toIdx > fromIdx) {
        insertIdx = Math.max(0, insertIdx - 1);
      }
      insertIdx = Math.min(insertIdx, toSection.items.length);

      toSection.items.splice(insertIdx, 0, item);
      return next;
    });

    setDragItem(null);
    setDragOverItem(null);
  };

  // Drag & drop for sections
  const handleSectionDragStart = (idx: number) => {
    setDragSection(idx);
  };

  const handleSectionDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverSection(idx);
  };

  const handleSectionDrop = () => {
    if (dragSection === null || dragOverSection === null || dragSection === dragOverSection) {
      setDragSection(null);
      setDragOverSection(null);
      return;
    }
    updateSections(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragSection, 1);
      next.splice(dragOverSection, 0, moved);
      return next;
    });
    setDragSection(null);
    setDragOverSection(null);
  };

  const resetToDefault = () => {
    setSections(DEFAULT_SECTIONS);
    saveSections(DEFAULT_SECTIONS);
  };

  return (
    <aside className="flex h-screen w-60 flex-col border-r border-gray-200 bg-white">
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-gray-100 px-5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">ERC</p>
            <p className="text-xs text-gray-500">Research Copilot</p>
          </div>
        </div>
        <button
          onClick={() => setEditMode(!editMode)}
          className={clsx(
            "rounded-md p-1.5 transition-colors",
            editMode
              ? "bg-indigo-100 text-indigo-600"
              : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          )}
          title={editMode ? "Kaydet" : "Menüyü düzenle"}
        >
          {editMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {/* Favoriler (en ustte, renklendirilmis) */}
        {!editMode && favItems.length > 0 && (
          <div className="mb-4">
            <p className="mb-1 flex items-center gap-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-amber-500">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> Favoriler
            </p>
            <div className="space-y-0.5">
              {favItems.map((item) => {
                const Icon = getIcon(item.icon);
                return (
                  <NavLink
                    key={`fav-${item.href}`}
                    to={item.href}
                    className={({ isActive }) =>
                      clsx(
                        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive ? "bg-amber-100 text-amber-800" : "text-amber-700 hover:bg-amber-50"
                      )
                    }
                  >
                    <Icon className="h-4 w-4 flex-shrink-0 text-amber-500" />
                    <span className="flex-1 truncate">{item.label}</span>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.href); }}
                      title="Favoriden çıkar"
                      className="flex-shrink-0 rounded p-0.5 opacity-0 group-hover:opacity-100"
                    >
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    </button>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {shownSections.map((section, si) => (
          <div
            key={section.id}
            className={clsx(
              si > 0 ? "mt-4" : "",
              editMode && dragOverSection === si && dragSection !== null && "border-t-2 border-indigo-400",
            )}
            draggable={editMode && !dragItem}
            onDragStart={() => editMode && handleSectionDragStart(si)}
            onDragOver={(e) => { if (editMode && dragSection !== null) handleSectionDragOver(e, si); }}
            onDrop={() => { if (editMode && dragSection !== null) handleSectionDrop(); }}
            onDragEnd={() => { setDragSection(null); setDragOverSection(null); }}
          >
            {/* Section header */}
            {(section.title || editMode) && (
              <div className="mb-1 flex items-center gap-1 px-1">
                {editMode && (
                  <GripVertical className="h-3 w-3 cursor-grab text-gray-300 flex-shrink-0" />
                )}
                {editMode && editingTitle === section.id ? (
                  <input
                    ref={titleInputRef}
                    className="flex-1 rounded border border-indigo-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600 outline-none focus:ring-1 focus:ring-indigo-400"
                    defaultValue={section.title || ""}
                    onBlur={(e) => {
                      renameSection(section.id, e.target.value);
                      setEditingTitle(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        renameSection(section.id, e.currentTarget.value);
                        setEditingTitle(null);
                      }
                      if (e.key === "Escape") setEditingTitle(null);
                    }}
                  />
                ) : (
                  <p
                    className={clsx(
                      "flex-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-2",
                      editMode && "cursor-pointer hover:text-indigo-500"
                    )}
                    onClick={() => editMode && setEditingTitle(section.id)}
                  >
                    {section.title || "(İsimsiz)"}
                  </p>
                )}
                {editMode && (
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => moveSectionUp(si)} className="p-0.5 text-gray-300 hover:text-gray-500" title="Yukarı">
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button onClick={() => moveSectionDown(si)} className="p-0.5 text-gray-300 hover:text-gray-500" title="Aşağı">
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {section.items.length === 0 && (
                      <button onClick={() => removeSection(section.id)} className="p-0.5 text-gray-300 hover:text-red-500" title="Sil">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Section items */}
            <div
              className={clsx("space-y-0.5", editMode && section.items.length === 0 && "min-h-[32px] rounded border border-dashed border-gray-200")}
              onDragOver={(e) => { if (editMode && dragItem && section.items.length === 0) handleItemDragOverSection(e, section.id); }}
              onDrop={() => { if (editMode && dragItem) handleItemDrop(); }}
            >
              {section.items.map((item, ii) => {
                const IconComp = getIcon(item.icon);
                const isDragOver = dragOverItem?.sectionId === section.id && dragOverItem?.itemIdx === ii;

                if (editMode) {
                  return (
                    <div
                      key={item.href}
                      className={clsx(
                        "flex items-center gap-1 rounded-lg px-1 py-1.5 text-sm text-gray-600 group",
                        isDragOver && dragItem && "border-t-2 border-indigo-400",
                      )}
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); handleItemDragStart(section.id, ii); }}
                      onDragOver={(e) => { e.stopPropagation(); handleItemDragOver(e, section.id, ii); }}
                      onDrop={(e) => { e.stopPropagation(); handleItemDrop(); }}
                      onDragEnd={(e) => { e.stopPropagation(); setDragItem(null); setDragOverItem(null); }}
                    >
                      <GripVertical className="h-3.5 w-3.5 cursor-grab text-gray-300 flex-shrink-0" />
                      <IconComp className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="flex-1 truncate text-xs">{item.label}</span>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                        <button onClick={() => moveItemUp(section.id, ii)} className="p-0.5 text-gray-300 hover:text-gray-500">
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button onClick={() => moveItemDown(section.id, ii)} className="p-0.5 text-gray-300 hover:text-gray-500">
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <button onClick={() => removeItem(section.id, ii)} className="p-0.5 text-gray-300 hover:text-red-500">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  );
                }

                const isFav = favorites.includes(item.href);
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={({ isActive }) =>
                      clsx(
                        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-indigo-50 text-indigo-700"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )
                    }
                  >
                    <IconComp className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.href); }}
                      title={isFav ? "Favoriden çıkar" : "Favorilere ekle"}
                      className={clsx("flex-shrink-0 rounded p-0.5 transition-opacity", isFav ? "opacity-100" : "opacity-0 group-hover:opacity-100")}
                    >
                      <Star className={clsx("h-3.5 w-3.5", isFav ? "fill-amber-400 text-amber-400" : "text-gray-300 hover:text-amber-400")} />
                    </button>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}

        {/* Taki magazasi seciliyken tekstil sayfalari gizlenir. Erisimi tamamen
            kapatmiyoruz: kullanici tek tikla hepsini geri acabilsin, aksi halde
            bir Etsy sayfasina ulasmak icin magaza degistirmek zorunda kalir. */}
        {(isTrendyol || isConceptVertical) && !editMode && (
          <div className="mt-4 px-1">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-2 py-1.5 text-[10px] text-gray-500 transition-colors hover:border-indigo-400 hover:text-indigo-600"
              title={shop ? `${shop.shopName} — ${isTrendyol ? "Trendyol" : "Etsy takı"} mağazası` : undefined}
            >
              {showAll ? "Yalnızca takı sayfaları" : "Tüm sayfaları göster"}
            </button>
          </div>
        )}

        {/* Edit mode: add group & reset */}
        {editMode && (
          <div className="mt-4 space-y-2 px-1">
            <button
              onClick={addSection}
              className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 transition-colors hover:border-indigo-400 hover:text-indigo-600"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              Yeni Grup Ekle
            </button>
            <button
              onClick={resetToDefault}
              className="flex w-full items-center justify-center rounded-lg px-3 py-1.5 text-[10px] text-gray-400 transition-colors hover:text-red-500"
            >
              Varsayılana Sıfırla
            </button>
          </div>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-gray-100 p-3">
        {/* Derleme damgasi: tarayici eski paketi calistiriyorsa hemen belli olur */}
        <p className="px-2 pb-1 text-[9px] text-gray-300" title={`Paket: ${__BUILD_TIME__}`}>
          derleme {__BUILD_TIME__.slice(0, 16).replace("T", " ")}
        </p>
        <div className="mb-2 flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-medium text-indigo-700">
            {user?.name?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? "U"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">
              {user?.name ?? "Kullanıcı"}
            </p>
            <p className="truncate text-xs text-gray-500">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <LogOut className="h-4 w-4" />
          Çıkış Yap
        </button>
      </div>
    </aside>
  );
}
